/* Pryme design system: shared by the end-user portal and the developer site.
   Direction and rationale: docs/DESIGN.md. Quiet surfaces, one accent, status colour only where it means something.
   Accessibility baseline: AA text contrast, visible focus, 44px targets on touch, reduced-motion aware. */
:root {
  /* Ink and surfaces */
  --p-ink: #0f1a2a;
  --p-ink-2: #344154;
  --p-ink-3: #5b6676;
  --p-ink-4: #8a93a0;
  --p-bg: #f4f5f7;
  --p-surface: #ffffff;
  --p-surface-2: #f8f9fb;
  --p-surface-3: #eef0f3;
  --p-line: #e2e5ea;
  --p-line-2: #cfd4db;

  /* Accent: deep institutional blue */
  --p-accent: #1f4e8c;
  --p-accent-hover: #183f72;
  --p-accent-soft: #edf2f9;
  --p-accent-line: #c5d4e9;
  --p-accent-ink: #ffffff;
  --p-focus: #2b67c4;

  /* Status */
  --p-ok: #17663f;
  --p-ok-soft: #ecf6f0;
  --p-ok-line: #bcdcc9;
  --p-warn: #8a5300;
  --p-warn-soft: #fbf4e4;
  --p-warn-line: #e8d2a0;
  --p-bad: #a8241a;
  --p-bad-soft: #fcefee;
  --p-bad-line: #efc4c0;
  --p-info-soft: #eef3fa;
  --p-info-line: #c9d7ec;
  --p-sandbox: #7a4d00;
  --p-sandbox-soft: #fbf3dc;
  --p-sandbox-line: #e6cf92;

  /* Geometry */
  --p-radius-xs: 4px;
  --p-radius-sm: 6px;
  --p-radius: 8px;
  --p-shadow: 0 1px 2px rgba(15, 26, 42, 0.04);
  --p-shadow-pop: 0 6px 24px rgba(15, 26, 42, 0.1);
  --p-header-h: 56px;
  --p-band-h: 30px;
  /* Height of the sticky chrome: the topbar, plus the sandbox band when present. */
  --p-chrome-h: var(--p-header-h);

  --p-font: -apple-system, BlinkMacSystemFont, 'Segoe UI Variable Text', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  --p-mono: ui-monospace, 'SF Mono', SFMono-Regular, 'Cascadia Mono', 'Roboto Mono', Menlo, Consolas, monospace;

  /* Legacy aliases (kept so older inline styles keep working) */
  --p-brand: var(--p-accent);
  --p-brand-soft: var(--p-accent-soft);
  --p-brand-ink: var(--p-accent-ink);
  --p-night: var(--p-ink);
  color-scheme: light;
}

* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
html { -webkit-text-size-adjust: 100%; }
body {
  font-family: var(--p-font);
  font-size: 0.9375rem;
  line-height: 1.55;
  color: var(--p-ink-2);
  background: var(--p-bg);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}
body:has(.p-envbar) { --p-chrome-h: calc(var(--p-header-h) + var(--p-band-h)); }
a { color: var(--p-accent); text-decoration-thickness: 1px; text-underline-offset: 2px; }
a:hover { color: var(--p-accent-hover); }
strong, b { color: var(--p-ink); font-weight: 600; }
.p-alert.bad strong, .p-verified strong, .p-unverified strong { color: inherit; }
code, pre, .mono { font-family: var(--p-mono); font-size: 0.86em; }
h1, h2, h3, h4 { color: var(--p-ink); line-height: 1.25; margin: 0 0 0.5rem; font-weight: 600; letter-spacing: -0.006em; }
h1 { font-size: 1.625rem; letter-spacing: -0.014em; }
h2 { font-size: 1.0625rem; }
h3 { font-size: 0.9375rem; }
h4 { font-size: 0.875rem; }
p { margin: 0 0 0.75rem; }
ul, ol { padding-left: 1.25rem; }
li { margin-bottom: 0.2rem; }
hr { border: 0; border-top: 1px solid var(--p-line); }
:focus-visible { outline: 2px solid var(--p-focus); outline-offset: 2px; border-radius: 3px; }
::selection { background: #cfdcf0; }
@media (prefers-reduced-motion: reduce) { * { transition: none !important; animation: none !important; } }

.p-skip { position: absolute; left: -9999px; }
.p-skip:focus { left: 1rem; top: 1rem; background: #fff; padding: 0.5rem 1rem; z-index: 100; border: 1px solid var(--p-line-2); border-radius: var(--p-radius-sm); }
.p-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.p-tabular { font-variant-numeric: tabular-nums; }

/* ------------------------------------------------------------------ Shell */
.p-shell { min-height: 100vh; display: grid; grid-template-rows: auto 1fr; grid-template-columns: minmax(0, 1fr); }
.p-header { position: sticky; top: 0; z-index: 20; }
.p-header > .p-topbar { position: static; }
.p-topbar {
  display: flex; align-items: center; gap: 1rem; height: var(--p-header-h); padding: 0 1.25rem;
  background: var(--p-surface); color: var(--p-ink); border-bottom: 1px solid var(--p-line);
  position: sticky; top: 0; z-index: 20;
}
.p-topbar a { color: var(--p-ink-2); text-decoration: none; }
.p-topbar a:hover { color: var(--p-ink); }
.p-topbar .p-spacer { flex: 1; }
.p-topbar-link { font-size: 0.875rem; display: inline-flex; align-items: center; gap: 0.3rem; }
.p-topbar-sep { width: 1px; height: 20px; background: var(--p-line); }
.p-topbar-user { font-size: 0.875rem; color: var(--p-ink-3); display: inline-flex; align-items: center; gap: 0.4rem; }
@media (max-width: 640px) { .p-topbar { gap: 0.6rem; padding: 0 0.75rem; } .p-hide-sm { display: none !important; } }

/* Brand mark + wordmark */
.p-logo { display: inline-flex; align-items: center; gap: 0.55rem; color: var(--p-ink); font-weight: 650; font-size: 1.0625rem; letter-spacing: -0.01em; text-decoration: none; white-space: nowrap; }
.p-logo-mark { width: 26px; height: 26px; flex: none; display: block; }
.p-logo-suffix { font-weight: 450; color: var(--p-ink-3); padding-left: 0.55rem; border-left: 1px solid var(--p-line-2); letter-spacing: 0; }

/* Environment band (sandbox) and tags */
.p-envbar, .p-sandbox-strip {
  display: flex; justify-content: center; align-items: center; gap: 0.5rem; flex-wrap: wrap;
  background: var(--p-sandbox-soft); color: var(--p-sandbox); border-bottom: 1px solid var(--p-sandbox-line);
  font-size: 0.8125rem; padding: 0.25rem 1rem; text-align: center; line-height: 1.4; min-height: var(--p-band-h);
}
.p-envbar strong, .p-sandbox-strip strong { color: var(--p-sandbox); font-weight: 650; letter-spacing: 0.04em; text-transform: uppercase; font-size: 0.75rem; }
.p-env-tag { display: inline-flex; align-items: center; gap: 0.35rem; height: 24px; padding: 0 0.55rem; border-radius: var(--p-radius-xs); font-size: 0.75rem; font-weight: 600; border: 1px solid; white-space: nowrap; }
.p-env-tag.sandbox { color: var(--p-sandbox); background: var(--p-sandbox-soft); border-color: var(--p-sandbox-line); }
.p-env-tag.production { color: var(--p-ink-2); background: var(--p-surface); border-color: var(--p-line-2); }
.p-env-tag .p-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.p-env-tag.production .p-dot { background: var(--p-ok); }

/* Sidebar navigation */
.p-body { display: grid; grid-template-columns: minmax(0, 1fr); min-width: 0; }
.p-nav { display: flex; gap: 0.15rem; overflow-x: auto; padding: 0.5rem 0.75rem; background: var(--p-surface); border-bottom: 1px solid var(--p-line); }
.p-nav a {
  white-space: nowrap; padding: 0.45rem 0.7rem; border-radius: var(--p-radius-sm); color: var(--p-ink-2);
  text-decoration: none; font-weight: 500; font-size: 0.875rem; display: flex; align-items: center; gap: 0.55rem;
}
.p-nav > * { flex-shrink: 0; }
.p-nav a:hover { background: var(--p-surface-2); color: var(--p-ink); }
.p-nav a.active, .p-nav a[aria-current='page'] { background: var(--p-accent-soft); color: var(--p-accent); font-weight: 600; }
.p-nav a .p-icon { color: var(--p-ink-4); }
.p-nav a.active .p-icon, .p-nav a[aria-current='page'] .p-icon { color: var(--p-accent); }
.p-nav-label { display: none; }
.p-nav-badge { margin-left: auto; background: var(--p-accent); color: #fff; border-radius: 10px; padding: 0 0.42rem; font-size: 0.7rem; font-weight: 650; line-height: 1.35rem; min-width: 1.35rem; text-align: center; font-variant-numeric: tabular-nums; }
.p-nav-badge.attention { background: var(--p-warn); }
.p-main { padding: 1.25rem 1rem 3rem; max-width: 1120px; width: 100%; margin: 0 auto; min-width: 0; }
.p-main.narrow { max-width: 760px; }
@media (min-width: 960px) {
  .p-body { grid-template-columns: 248px minmax(0, 1fr); }
  .p-nav {
    flex-direction: column; border-bottom: 0; border-right: 1px solid var(--p-line); padding: 1rem 0.75rem 2rem;
    height: calc(100vh - var(--p-chrome-h)); position: sticky; top: var(--p-chrome-h); align-self: start; overflow-y: auto;
  }
  .p-nav-label { display: block; font-size: 0.6875rem; font-weight: 650; letter-spacing: 0.07em; text-transform: uppercase; color: var(--p-ink-4); padding: 1rem 0.7rem 0.35rem; }
  .p-nav-label:first-child { padding-top: 0.25rem; }
  .p-main { padding: 2rem 2.5rem 4rem; }
}

/* Page header */
.p-page-head { display: flex; flex-wrap: wrap; gap: 1rem; justify-content: space-between; align-items: flex-end; margin-bottom: 1.5rem; }
.p-page-head > div:first-child { min-width: 0; flex: 1 1 420px; }
.p-page-head h1 { margin-bottom: 0.35rem; }
.p-lead { color: var(--p-ink-3); font-size: 0.96875rem; max-width: 70ch; margin: 0; }
.p-eyebrow { display: block; font-size: 0.6875rem; font-weight: 650; letter-spacing: 0.08em; text-transform: uppercase; color: var(--p-ink-3); margin-bottom: 0.4rem; }
.p-back { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.875rem; text-decoration: none; margin-bottom: 1rem; }

/* ------------------------------------------------------------------ Surfaces */
.p-card { background: var(--p-surface); border: 1px solid var(--p-line); border-radius: var(--p-radius); padding: 1.25rem 1.5rem; box-shadow: var(--p-shadow); margin-bottom: 1rem; }
.p-card > h2:first-child, .p-card > .p-row:first-child h2 { margin-bottom: 0.75rem; }
.p-card.flush { padding: 0; overflow: hidden; }
.p-card.muted { background: var(--p-surface-2); box-shadow: none; }
.p-card-head { display: flex; justify-content: space-between; align-items: center; gap: 0.75rem; flex-wrap: wrap; padding: 0.9rem 1.5rem; border-bottom: 1px solid var(--p-line); background: var(--p-surface-2); }
.p-card-head h2, .p-card-head h3 { margin: 0; font-size: 0.9375rem; }
.p-card-body { padding: 1.25rem 1.5rem; }
.p-card-foot { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; padding: 0.9rem 1.5rem; border-top: 1px solid var(--p-line); background: var(--p-surface-2); }
.p-section-title { font-size: 0.6875rem; font-weight: 650; letter-spacing: 0.08em; text-transform: uppercase; color: var(--p-ink-3); margin: 2rem 0 0.75rem; }
.p-grid { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
.p-row { display: flex; gap: 0.75rem; align-items: center; flex-wrap: wrap; }
.p-between { justify-content: space-between; }
.p-stack > * + * { margin-top: 0.75rem; }
.p-muted { color: var(--p-ink-3); }
.p-small { font-size: 0.8125rem; }
.p-divider { border: 0; border-top: 1px solid var(--p-line); margin: 1.25rem 0; }
.p-list { list-style: none; margin: 0; padding: 0; }
.p-list > li { padding: 0.85rem 0; border-bottom: 1px solid var(--p-line); margin: 0; }
.p-list > li:first-child { padding-top: 0.25rem; }
.p-list > li:last-child { border-bottom: 0; padding-bottom: 0.25rem; }
.p-kv { display: grid; grid-template-columns: minmax(130px, max-content) 1fr; gap: 0.5rem 1.5rem; font-size: 0.875rem; margin: 0; }
.p-kv dt { color: var(--p-ink-3); }
.p-kv dd { margin: 0; word-break: break-word; color: var(--p-ink); }
@media (max-width: 640px) { .p-kv { grid-template-columns: 1fr; gap: 0.1rem; } .p-kv dt { margin-top: 0.5rem; } }
.p-stat { font-size: 1.625rem; font-weight: 600; color: var(--p-ink); font-variant-numeric: tabular-nums; letter-spacing: -0.01em; line-height: 1.2; }
.p-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); background: var(--p-surface); border: 1px solid var(--p-line); border-radius: var(--p-radius); margin-bottom: 1rem; overflow: hidden; }
.p-stats > * { padding: 1rem 1.25rem; border-right: 1px solid var(--p-line); text-decoration: none; color: inherit; display: grid; gap: 0.15rem; }
.p-stats > *:last-child { border-right: 0; }
.p-stats > a:hover { background: var(--p-surface-2); }
.p-stats .p-stat-label { font-size: 0.8125rem; color: var(--p-ink-3); }
@media (max-width: 640px) { .p-stats > * { border-right: 0; border-bottom: 1px solid var(--p-line); } }

/* Facts strip: label above value, used on decision surfaces */
.p-facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); border: 1px solid var(--p-line); border-radius: var(--p-radius-sm); margin: 0; overflow: hidden; }
.p-facts > div { padding: 0.75rem 1rem; border-right: 1px solid var(--p-line); min-width: 0; }
.p-facts > div:last-child { border-right: 0; }
.p-facts dt { font-size: 0.6875rem; font-weight: 650; letter-spacing: 0.07em; text-transform: uppercase; color: var(--p-ink-3); margin-bottom: 0.2rem; }
.p-facts dd { margin: 0; color: var(--p-ink); font-weight: 600; word-break: break-word; }
.p-facts dd .p-sub { display: flex; align-items: center; gap: 0.3rem; font-weight: 400; font-size: 0.8125rem; color: var(--p-ink-3); margin-top: 0.15rem; }
@media (max-width: 640px) { .p-facts > div { border-right: 0; border-bottom: 1px solid var(--p-line); } .p-facts > div:last-child { border-bottom: 0; } }

/* Trust indicators: one quiet row */
.p-trust { display: flex; flex-wrap: wrap; gap: 0.5rem 1.5rem; list-style: none; margin: 0; padding: 0; font-size: 0.8125rem; color: var(--p-ink-2); }
.p-trust li { display: inline-flex; align-items: center; gap: 0.4rem; margin: 0; }
.p-trust .p-icon { color: var(--p-ok); }

/* Public pages: hero, features, steps */
.p-hero { background: var(--p-surface); border-bottom: 1px solid var(--p-line); padding: 4rem 1.25rem 3.25rem; }
.p-hero-inner { max-width: 1040px; margin: 0 auto; }
.p-stop { color: #2f6fd0; }
.p-hero h1 { font-size: clamp(2rem, 4.2vw, 2.875rem); letter-spacing: -0.024em; line-height: 1.1; margin: 0.25rem 0 1rem; max-width: 20ch; }
.p-hero .p-tagline, .p-hero .p-lead { font-size: 1.0625rem; color: var(--p-ink-2); max-width: 62ch; margin-bottom: 1.75rem; }
.p-hero .p-trust { margin-top: 2rem; padding-top: 1.25rem; border-top: 1px solid var(--p-line); }
.p-features { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 0; border: 1px solid var(--p-line); border-radius: var(--p-radius); background: var(--p-surface); overflow: hidden; margin-bottom: 1.5rem; }
.p-feature { padding: 1.4rem 1.5rem; border-right: 1px solid var(--p-line); border-bottom: 1px solid var(--p-line); margin: -1px -1px 0 0; }
.p-feature h3 { margin: 0.75rem 0 0.35rem; font-size: 0.96875rem; }
.p-feature p { font-size: 0.875rem; color: var(--p-ink-3); margin: 0; }
.p-feature-icon { width: 34px; height: 34px; border-radius: var(--p-radius-sm); background: var(--p-accent-soft); color: var(--p-accent); display: inline-flex; align-items: center; justify-content: center; }
.p-howto { counter-reset: p-howto; list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1.25rem; }
.p-howto li { counter-increment: p-howto; padding-top: 0.9rem; border-top: 2px solid var(--p-ink); margin: 0; }
.p-howto li::before { content: counter(p-howto, decimal-leading-zero); display: block; font-size: 0.75rem; font-weight: 650; color: var(--p-ink-3); letter-spacing: 0.06em; margin-bottom: 0.35rem; font-variant-numeric: tabular-nums; }
.p-howto strong { display: block; margin-bottom: 0.2rem; }
.p-howto span { font-size: 0.875rem; color: var(--p-ink-3); }
.p-footer { border-top: 1px solid var(--p-line); padding: 1.5rem 1.25rem 2rem; font-size: 0.8125rem; color: var(--p-ink-3); background: var(--p-surface); }
.p-footer-inner { max-width: 1040px; margin: 0 auto; display: flex; flex-wrap: wrap; gap: 0.5rem 1.5rem; justify-content: space-between; align-items: center; }
.p-footer a { color: var(--p-ink-2); }

/* ------------------------------------------------------------------ Controls */
.p-btn {
  appearance: none; border: 1px solid var(--p-accent); background: var(--p-accent); color: var(--p-accent-ink);
  padding: 0 1rem; min-height: 40px; border-radius: var(--p-radius-sm); font: inherit; font-size: 0.875rem; font-weight: 600;
  cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 0.45rem; text-decoration: none;
  white-space: nowrap; transition: background-color 0.12s, border-color 0.12s, color 0.12s; line-height: 1.2;
}
.p-btn:hover { background: var(--p-accent-hover); border-color: var(--p-accent-hover); color: var(--p-accent-ink); }
.p-btn:disabled, .p-btn[aria-disabled='true'] { opacity: 0.5; cursor: not-allowed; }
.p-btn:disabled:hover { background: var(--p-accent); border-color: var(--p-accent); }
.p-btn.secondary { background: var(--p-surface); color: var(--p-ink); border-color: var(--p-line-2); }
.p-btn.secondary:hover { background: var(--p-surface-2); border-color: var(--p-ink-4); color: var(--p-ink); }
.p-btn.secondary:disabled:hover { background: var(--p-surface); border-color: var(--p-line-2); }
.p-btn.ghost { background: transparent; color: var(--p-accent); border-color: transparent; }
.p-btn.ghost:hover { background: var(--p-accent-soft); color: var(--p-accent-hover); }
.p-btn.ghost:disabled:hover { background: transparent; }
.p-btn.danger { background: var(--p-bad); border-color: var(--p-bad); color: #fff; }
.p-btn.danger:hover { background: #8c1d14; border-color: #8c1d14; }
.p-btn.danger-outline { background: var(--p-surface); color: var(--p-bad); border-color: var(--p-bad-line); }
.p-btn.danger-outline:hover { background: var(--p-bad-soft); border-color: var(--p-bad); color: var(--p-bad); }
.p-btn.small { min-height: 32px; padding: 0 0.7rem; font-size: 0.8125rem; }
.p-btn.large { min-height: 46px; padding: 0 1.35rem; font-size: 0.9375rem; }
@media (pointer: coarse) { .p-btn { min-height: 44px; } .p-btn.small { min-height: 40px; } }
.p-btn-group { display: inline-flex; border: 1px solid var(--p-line-2); border-radius: var(--p-radius-sm); overflow: hidden; background: var(--p-surface); }
.p-btn-group .p-btn { border: 0; border-radius: 0; min-height: 30px; background: transparent; color: var(--p-ink-2); font-weight: 550; }
.p-btn-group .p-btn + .p-btn { border-left: 1px solid var(--p-line-2); }
.p-btn-group .p-btn[aria-pressed='true'] { background: var(--p-ink); color: #fff; }
.p-btn-group .p-btn:disabled { opacity: 1; color: var(--p-ink-4); }

.p-field { display: grid; gap: 0.35rem; margin-bottom: 1rem; align-content: start; }
.p-field label, .p-label { font-weight: 600; font-size: 0.875rem; color: var(--p-ink); }
.p-field .p-hint, .p-hint { font-size: 0.8125rem; color: var(--p-ink-3); }
.p-input, .p-select, .p-textarea, .p-field input:not([type='checkbox']):not([type='radio']), .p-field select {
  font: inherit; font-size: 0.9375rem; padding: 0.5rem 0.75rem; border: 1px solid var(--p-line-2); border-radius: var(--p-radius-sm);
  background: var(--p-surface); color: var(--p-ink); width: 100%; min-height: 40px; transition: border-color 0.12s, box-shadow 0.12s;
}
.p-input::placeholder, .p-textarea::placeholder { color: var(--p-ink-4); }
.p-input:hover, .p-select:hover, .p-textarea:hover { border-color: var(--p-ink-4); }
.p-input:focus, .p-select:focus, .p-textarea:focus, .p-field input:focus, .p-field select:focus { outline: none; border-color: var(--p-accent); box-shadow: 0 0 0 3px rgba(31, 78, 140, 0.16); }
.p-textarea { min-height: 96px; font-family: var(--p-mono); font-size: 0.8125rem; line-height: 1.5; }
.p-check { display: flex; gap: 0.6rem; align-items: flex-start; padding: 0.4rem 0; cursor: pointer; }
.p-check input { margin-top: 0.2rem; width: 16px; height: 16px; flex: none; accent-color: var(--p-accent); }
input[type='radio'], input[type='checkbox'] { accent-color: var(--p-accent); }
@media (pointer: coarse) { .p-input, .p-select { min-height: 44px; } }
fieldset { min-width: 0; }

/* ------------------------------------------------------------------ Badges, alerts, status */
.p-badge {
  display: inline-flex; align-items: center; gap: 0.3rem; height: 22px; padding: 0 0.5rem; border-radius: var(--p-radius-xs);
  font-size: 0.75rem; font-weight: 600; background: var(--p-accent-soft); color: var(--p-accent); border: 1px solid var(--p-accent-line);
  white-space: nowrap; line-height: 1; vertical-align: middle;
}
.p-badge .p-icon { flex: none; }
.p-badge.ok { background: var(--p-ok-soft); color: var(--p-ok); border-color: var(--p-ok-line); }
.p-badge.warn { background: var(--p-warn-soft); color: var(--p-warn); border-color: var(--p-warn-line); }
.p-badge.bad { background: var(--p-bad-soft); color: var(--p-bad); border-color: var(--p-bad-line); }
.p-badge.neutral { background: var(--p-surface-3); color: var(--p-ink-2); border-color: var(--p-line); }
.p-badge.sandbox { background: var(--p-sandbox-soft); color: var(--p-sandbox); border-color: var(--p-sandbox-line); }
.p-status { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.8125rem; font-weight: 550; color: var(--p-ink-2); white-space: nowrap; }
.p-status::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: var(--p-ink-4); }
.p-status.ok::before { background: var(--p-ok); }
.p-status.warn::before { background: #c98a12; }
.p-status.bad::before { background: var(--p-bad); }
.p-verified { display: inline-flex; align-items: center; gap: 0.3rem; color: var(--p-ok); font-weight: 600; font-size: 0.8125rem; }
.p-unverified { display: inline-flex; align-items: center; gap: 0.3rem; color: var(--p-warn); font-weight: 600; font-size: 0.8125rem; }

.p-alert { border-radius: var(--p-radius-sm); padding: 0.75rem 1rem; margin-bottom: 1rem; border: 1px solid; border-left-width: 3px; color: var(--p-ink-2); font-size: 0.875rem; }
.p-alert strong { color: var(--p-ink); }
.p-alert.info { background: var(--p-info-soft); border-color: var(--p-info-line); border-left-color: var(--p-accent); }
.p-alert.ok { background: var(--p-ok-soft); border-color: var(--p-ok-line); border-left-color: var(--p-ok); }
.p-alert.warn { background: var(--p-warn-soft); border-color: var(--p-warn-line); border-left-color: #c98a12; }
.p-alert.bad { background: var(--p-bad-soft); border-color: var(--p-bad-line); border-left-color: var(--p-bad); }
.p-alert > :last-child { margin-bottom: 0; }

/* ------------------------------------------------------------------ Code, secrets, identifiers */
.p-code { background: #0f1a2a; color: #dce4ef; border-radius: var(--p-radius-sm); padding: 0.9rem 1rem; overflow-x: auto; font-family: var(--p-mono); font-size: 0.8125rem; line-height: 1.6; white-space: pre; margin: 0; }
.p-secret { font-family: var(--p-mono); font-size: 0.875rem; color: var(--p-ink); background: #fffbf0; border: 1px solid var(--p-warn-line); padding: 0.6rem 0.75rem; border-radius: var(--p-radius-sm); word-break: break-all; }
.p-mono-chip { font-family: var(--p-mono); font-size: 0.8125rem; color: var(--p-ink-2); background: var(--p-surface-3); border: 1px solid var(--p-line); padding: 0.05rem 0.35rem; border-radius: var(--p-radius-xs); overflow-wrap: anywhere; }
.p-fingerprint { font-family: var(--p-mono); font-size: 0.875rem; letter-spacing: 0.02em; color: var(--p-ink); word-break: break-all; }

/* ------------------------------------------------------------------ Tables */
.p-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; font-variant-numeric: tabular-nums; }
.p-table th, .p-table td { text-align: left; padding: 0.65rem 0.75rem; border-bottom: 1px solid var(--p-line); vertical-align: top; }
.p-table th { color: var(--p-ink-3); font-weight: 600; font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.07em; background: var(--p-surface-2); white-space: nowrap; }
.p-table tbody tr:hover td { background: var(--p-surface-2); }
.p-table tbody tr:last-child td { border-bottom: 0; }
.p-table caption { padding: 0 0 0.5rem; }
.p-table-wrap { overflow-x: auto; border: 1px solid var(--p-line); border-radius: var(--p-radius-sm); }
.p-card > .p-table-wrap:last-child { margin-bottom: 0; }

/* ------------------------------------------------------------------ Timeline */
.p-timeline { list-style: none; padding: 0; margin: 0; border-left: 1px solid var(--p-line-2); margin-left: 0.3rem; }
.p-timeline li { position: relative; padding: 0 0 1.1rem 1.25rem; margin: 0; }
.p-timeline li:last-child { padding-bottom: 0.25rem; }
.p-timeline li::before { content: ''; position: absolute; left: -4.5px; top: 0.45rem; width: 8px; height: 8px; border-radius: 50%; background: var(--p-surface); border: 2px solid var(--p-ink-4); }
.p-timeline li:first-child::before { border-color: var(--p-accent); background: var(--p-accent); }

/* ------------------------------------------------------------------ Misc */
.p-spinner { width: 16px; height: 16px; border: 2px solid currentColor; border-right-color: transparent; border-radius: 50%; animation: p-spin 0.8s linear infinite; display: inline-block; flex: none; }
@keyframes p-spin { to { transform: rotate(360deg); } }
.p-empty { text-align: center; padding: 2.25rem 1.5rem; color: var(--p-ink-3); border: 1px dashed var(--p-line-2); border-radius: var(--p-radius); background: var(--p-surface); margin-bottom: 1rem; font-size: 0.875rem; }
.p-empty strong { display: block; color: var(--p-ink); font-size: 0.9375rem; margin-bottom: 0.25rem; }
.p-empty p { max-width: 52ch; margin: 0 auto 0.75rem; }
.p-empty > :last-child { margin-bottom: 0; }

/* Stepper */
.p-steps { display: flex; gap: 0.25rem 0.5rem; flex-wrap: wrap; margin: 0 0 1.5rem; padding: 0; list-style: none; counter-reset: p-step; }
.p-steps li { counter-increment: p-step; display: inline-flex; align-items: center; gap: 0.5rem; font-size: 0.8125rem; color: var(--p-ink-3); margin: 0; font-weight: 500; }
.p-steps li::before {
  content: counter(p-step); display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%;
  border: 1px solid var(--p-line-2); background: var(--p-surface); font-size: 0.75rem; font-weight: 600; color: var(--p-ink-3); font-variant-numeric: tabular-nums;
}
.p-steps li:not(:last-child)::after { content: ''; width: 28px; height: 1px; background: var(--p-line-2); margin-left: 0.25rem; }
.p-steps li.done { color: var(--p-ink-2); }
.p-steps li.done::before { content: '✓'; background: var(--p-ok-soft); border-color: var(--p-ok-line); color: var(--p-ok); }
.p-steps li.current { color: var(--p-ink); font-weight: 600; }
.p-steps li.current::before { background: var(--p-accent); border-color: var(--p-accent); color: #fff; }

.p-diagram { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
.p-diagram > div { border: 1px solid var(--p-line); border-radius: var(--p-radius-sm); padding: 0.9rem 1rem; background: var(--p-surface-2); }
.p-icon { display: inline-block; flex: none; vertical-align: -0.15em; }
details > summary { cursor: pointer; color: var(--p-accent); font-weight: 600; font-size: 0.875rem; }
details > summary:hover { color: var(--p-accent-hover); }
